import React, { useState, useEffect, useRef, FC } from "react";
import { Loader2, Scissors, Clock, Film, RotateCcw, ToggleLeft, ToggleRight, ChevronDown, ChevronRight, XCircle } from "lucide-react";
import type { ChatMessage, EditingSuggestion, ChatPendingChoice } from "../../types";

// Helper pour formater les timestamps
const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export interface MontageElements {
    intro: boolean;
    outro: boolean;
    synthe: boolean;
    panel: boolean;
}

interface ChatTabProps {
    messages?: ChatMessage[];
    onSendMessage?: (message: string, elements: MontageElements) => void;
    onChoiceSelect?: (choice: 'current' | 'original' | 'yes' | 'no', pendingChoice: ChatPendingChoice) => void;
    editingSuggestions?: EditingSuggestion[];
    onTranscriptionModeChange?: (useGpt4o: boolean) => void;
}

const ChatTab: FC<ChatTabProps> = ({
    messages = [],
    // FIX: Update default prop to match function signature and prevent type error.
    onSendMessage = (message: string, elements: MontageElements) => { },
    onChoiceSelect = (_choice: 'current' | 'original' | 'yes' | 'no', _pendingChoice: ChatPendingChoice) => { },
    editingSuggestions = [],
    onTranscriptionModeChange,
}) => {
    const [chatInput, setChatInput] = useState("");
    const chatInputRef = useRef<HTMLTextAreaElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(true);

    // Montage element toggles (default all disabled)
    const [montageElements, setMontageElements] = useState<MontageElements>({
        intro: false,
        outro: false,
        synthe: false,
        panel: false
    });

    // State pour le mode de transcription (persisté dans localStorage)
    const [useGpt4oTranscribe, setUseGpt4oTranscribe] = useState(() => {
        return localStorage.getItem('transcriptionMode') === 'gpt4o';
    });

    // Persister le choix et notifier le parent
    useEffect(() => {
        localStorage.setItem('transcriptionMode', useGpt4oTranscribe ? 'gpt4o' : 'whisper');
        onTranscriptionModeChange?.(useGpt4oTranscribe);
    }, [useGpt4oTranscribe, onTranscriptionModeChange]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    useEffect(() => {
        if (chatInputRef.current) {
            (chatInputRef.current as any).style.height = 'auto';
            (chatInputRef.current as any).style.height = `${(chatInputRef.current as any).scrollHeight}px`;
        }
    }, [chatInput]);

    const submitChat = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        const txt = chatInput.trim();
        if (!txt || !onSendMessage) return;
        onSendMessage(txt, montageElements);
        setChatInput("");
    };

    const handleChatKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submitChat();
        }
    };

    return (
        <div id="panel-assistant" role="tabpanel" aria-labelledby="tab-assistant" className="h-full flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900">Chat</h2>
                </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.map((m) => {
                    if (m.isLoading) {
                        return (
                            <div key={m.id} className="flex justify-start">
                                <div className="max-w-[80%] w-full rounded-2xl px-4 py-3 bg-gray-100 text-gray-900">
                                    <div className="flex items-center gap-3">
                                        <Loader2 className="w-5 h-5 animate-spin text-gray-500 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium">{m.statusText || 'Processing...'}</p>
                                            {typeof m.progress === 'number' && (
                                                <div className="flex items-center gap-2 mt-1">
                                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                        <div className="bg-black h-1.5 rounded-full transition-all duration-300" style={{ width: `${m.progress || 0}%` }}></div>
                                                    </div>
                                                    <span className="text-xs font-mono text-gray-500 w-9 text-right">{m.progress || 0}%</span>
                                                </div>
                                            )}
                                        </div>
                                        {m.onCancel && (
                                            <button
                                                onClick={m.onCancel}
                                                className="ml-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                                                title="Annuler"
                                            >
                                                <XCircle className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    // Render pending choice message with buttons
                    if (m.pendingChoice) {
                        const isEditBaseChoice = m.pendingChoice.type === 'edit-base-choice';
                        return (
                            <div key={m.id} className="flex justify-start">
                                <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-gray-100 text-gray-900">
                                    <p className="text-sm whitespace-pre-wrap mb-3">{m.text || ""}</p>
                                    <div className="flex flex-col gap-2">
                                        {m.pendingChoice.options.map((option) => (
                                            <button
                                                key={option.value}
                                                onClick={() => onChoiceSelect(option.value, m.pendingChoice!)}
                                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all text-left"
                                            >
                                                {isEditBaseChoice && option.value === 'current' ? (
                                                    <Film className="w-4 h-4 text-gray-600" />
                                                ) : isEditBaseChoice && option.value === 'original' ? (
                                                    <RotateCcw className="w-4 h-4 text-gray-600" />
                                                ) : null}
                                                <span className="text-sm font-medium text-gray-800">{option.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-black text-white" : "bg-gray-100 text-gray-900"}`}>
                                <p className="text-sm whitespace-pre-wrap">{m.text || ""}</p>
                                {m.action && (
                                    <button
                                        onClick={m.action.onClick}
                                        className="mt-3 px-4 py-2 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors text-sm"
                                    >
                                        {m.action.label}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="p-4 border-t border-gray-200">
                {editingSuggestions.length > 0 && (
                    <div className="mb-4">
                        <div
                            onClick={() => setIsSuggestionsOpen(!isSuggestionsOpen)}
                            className="w-full flex items-center justify-between group mb-2 hover:bg-gray-50 p-1 -mx-1 rounded cursor-pointer"
                        >
                            <div className="flex items-center justify-between w-full">
                                <h3 className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-2">
                                    <Scissors className="w-3.5 h-3.5" />
                                    SUGGESTIONS
                                </h3>

                                {/* Element Toggles */}
                                <div className="flex items-center gap-1 mr-2" onClick={(e) => e.stopPropagation()}>
                                    {(['intro', 'outro', 'synthe', 'panel'] as const).map((key) => (
                                        <button
                                            key={key}
                                            onClick={() => setMontageElements(prev => ({ ...prev, [key]: !prev[key] }))}
                                            className={`px-2 py-0.5 rounded text-[9px] font-semibold uppercase transition-all ${montageElements[key]
                                                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                                }`}
                                        >
                                            {key === 'synthe' ? 'Synthé' : key.charAt(0).toUpperCase() + key.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {isSuggestionsOpen ? <ChevronDown className="w-3 h-3 text-gray-400 group-hover:text-gray-600" /> : <ChevronRight className="w-3 h-3 text-gray-400 group-hover:text-gray-600" />}
                        </div>

                        {isSuggestionsOpen && (
                            <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                                {editingSuggestions.map((suggestion, index) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            setChatInput(suggestion.instruction);
                                            (chatInputRef.current as any)?.focus();
                                        }}
                                        className="w-full text-left bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 transition-all"
                                    >
                                        <span className="text-xs font-medium text-gray-900">
                                            {suggestion.label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                <form onSubmit={submitChat} className="flex items-end gap-2">
                    <textarea
                        ref={chatInputRef}
                        value={chatInput}
                        onChange={(e) => setChatInput((e.target as HTMLTextAreaElement).value)}
                        onKeyDown={handleChatKeyDown}
                        placeholder="Tapez votre message..."
                        rows={1}
                        className="flex-1 px-4 py-3 bg-black text-white border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent placeholder-gray-400 resize-none overflow-y-auto"
                        style={{ maxHeight: '150px' }}
                    />
                    <button type="submit" className="bg-black text-white px-6 py-3 rounded-xl hover:bg-gray-800 transition-all font-medium shrink-0">➤</button>
                </form>
            </div>
        </div>
    );
};

export default ChatTab;
